import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  TwModal as Modal,
  Text,
  Icon,
} from "@ui-component-library";
import { TailwindWrapper } from "@utils/TailwindWrapper";
import { useSubscription } from "../../context/SubscriptionCheckoutContext.js";
import {
  clearOrderFromCookie,
  saveOrderToCookie,
  trackSubscriptionCheckoutEvent,
  getStepName,
} from "./utils.js";
import { formatPriceWithCurrency } from "../../utils.js";
import { ConfirmClosePopup } from "./components/ConfirmClosePopup.js";
import Order from "./steps/Order";
import Payment from "./steps/Payment";
import ThankYou from "./steps/ThankYou";
import AccountStep from "./steps/AccountStep";
import { sendEvent } from "../../../../api";
import { useModalCloseOnEsc } from "../../utils.js";
import { useLogin } from "../../../login/context/LoginContext.js";
import { AlertDialog } from "@components/PropertySelector/components/AlertModal";

export default function SubscriptionCheckout() {
  const { isLogged } = useLogin() || { isLogged: false };

  const {
    currentStep,
    isCheckoutOpen,
    nextStep,
    previousStep,
    selectedProperties,
    orderPrices,
    paymentMethod,
    couponDiscount,
    extraData,
    setExtraData,
    setOpenSelectPropertyDialog,
    setStepCount,
    goToStep,
    resetCheckout,
    setStartedAsGuest,
    alertDialog,
    setAlertDialog,
  } = useSubscription();

  const steps = useMemo(
    () =>
      isLogged
        ? [Order, Payment, ThankYou]
        : [Order, AccountStep, Payment, ThankYou],
    [isLogged]
  );

  useEffect(() => {
    setStepCount(steps.length);
    // Clamp currentStep when steps array shrinks (e.g., user logged in during checkout)
    if (currentStep > steps.length) {
      goToStep(steps.length);
    }
  }, [steps.length, setStepCount, currentStep, goToStep]);

  // Capture auth state when checkout opens
  useEffect(() => {
    if (isCheckoutOpen && !isLogged) {
      setStartedAsGuest(true);
    }
  }, [isCheckoutOpen]);

  const CurrentStep = steps[currentStep - 1];

  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const isFirstStep = CurrentStep === Order;
  const isLastStep = CurrentStep === ThankYou;
  const shouldHideNavigation = CurrentStep === Payment || CurrentStep === ThankYou;
  const shouldHideFooter = CurrentStep === Payment || CurrentStep === AccountStep;
  const hidePriceSummary = !isFirstStep || orderPrices.discountPrice == null;

  // Track step changes and save order cookie before Payment
  useEffect(() => {
    if (!isCheckoutOpen) return;

    const stepName = getStepName(CurrentStep);
    trackSubscriptionCheckoutEvent("step_initiated", stepName);

    if (isLastStep) {
      trackSubscriptionCheckoutEvent("completed", stepName, {
        payment_method: paymentMethod,
      });
    }

    if (CurrentStep === Payment) {
      saveOrderToCookie({
        selectedProperties,
        extraData,
        orderPrices,
        paymentMethod,
        couponDiscount,
      });
    }
  }, [isCheckoutOpen, CurrentStep]);

  useEffect(() => {
    if (isCheckoutOpen && CurrentStep === Order) {
      sendEvent("plus_open_modal", { source: "plus" });
    } else if (isCheckoutOpen && CurrentStep === Payment) {
      sendEvent("viewed_payment_plus", { source: "plus" });
    }
  }, [isCheckoutOpen, CurrentStep]);

  const handleClose = () => {
    setIsConfirmCloseOpen(false);
    clearOrderFromCookie();
    resetCheckout();
    trackSubscriptionCheckoutEvent("cancelled", getStepName(CurrentStep));
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleNextStep = () => {
    if (CurrentStep === Order && selectedProperties.length === 0) {
      setOpenSelectPropertyDialog(true);
      return;
    }

    const hasErrors = selectedProperties.some((prop) => prop.plus_price_error);
    if (CurrentStep === Order && hasErrors) {
      setAlertDialog({
        title: "Något gick fel med beräkningen",
        subtitle: "Vi beklagar detta och ber dig att vänligen kontakta oss så återkommer vi till dig.",
        helperInfo: "Vänligen kontakta oss för hjälp med din beställning.",
      });
      return;
    }

    setExtraData({ createIfMissing: true });

    return nextStep();
  };

  const getNavigationButtonLabel = () => {
    if (isLastStep) {
      return "Stäng";
    }

    return "Nästa steg";
  };

  const handleOpenDemoLink = () => {
    window.open(
      "https://2e1iip.share-eu1.hsforms.com/2XeAiDeueTBmhUZMmOkm-Mw",
      "_blank",
      "noopener,noreferrer"
    );
  };

  useModalCloseOnEsc(isCheckoutOpen, handleClose)

  if (CurrentStep === Payment) {
    {/*
        Klarna HTML is rendered outside TailwindWrapper because Klarna Checkout cannot connect to its external API when inside a shadowRoot, which TailwindWrapper requires.
        which is used to isolate Tailwind styling here.
    */}
    return (
      <>
        <Payment
          isOpen={isCheckoutOpen}
          handleClose={handleConfirmClose}
        />
        <TailwindWrapper>
          <ConfirmClosePopup
            isOpen={isConfirmCloseOpen}
            setIsOpen={setIsConfirmCloseOpen}
            handleClose={handleClose}
          />
        </TailwindWrapper>
      </>
    );
  }

  return (
    <>
      <TailwindWrapper>
        <Modal tw isOpen={isCheckoutOpen} borderless={isFirstStep}>
          <Modal.Header tw className={shouldHideNavigation ? "hidden" : "px-4 py-3 md:p-6"}>
            <div className="flex w-full justify-between gap-4 items-start">
              <div className="flex flex-row gap-4 items-center">
                {!isFirstStep && (
                  <Button
                    tw
                    variant="tertiary"
                    size="icon"
                    onClick={previousStep}
                  >
                    <Icon
                      tw
                      name="ArrowRightSimple"
                      size="large"
                      className="rotate-180"
                    />
                  </Button>
                )}
                <div className="flex flex-col gap-1">
                  <Text variant="text-base/leading-normal/medium">
                    Bli Pluskund hos Treebula
                  </Text>
                </div>
              </div>
              <Button
                tw
                variant="tertiary"
                size="icon"
                onClick={handleConfirmClose}
              >
                <Icon
                  tw
                  name="CrossSimple"
                  className="color-charcoal-500"
                  size="large"
                />
              </Button>
            </div>
          </Modal.Header>
          <Modal.Content tw>
            {CurrentStep === Order && (
              <Order />
            )}
            {CurrentStep === AccountStep && <AccountStep />}
            {CurrentStep === ThankYou && <ThankYou />}
          </Modal.Content>
          {!shouldHideFooter && (
            <Modal.Footer tw>
              <div
                className={[
                  "flex flex-row justify-center items-center gap-2",
                  hidePriceSummary ? "opacity-0" : "",
                ].join(" ")}
              >
                <Text variant="text-lg/leading-none/medium" className='whitespace-nowrap'>
                  {formatPriceWithCurrency(orderPrices?.discountPrice)}
                </Text>
                <Text
                  variant="text-sm/leading-normal/normal"
                  className="text-charcoal-300"
                >
                  exkl. moms
                </Text>
              </div>
              <div className="flex flex-row gap-4">
                <Button tw variant="secondary" onClick={handleOpenDemoLink}>
                  Boka genomgång
                </Button>

                <Button
                  tw
                  variant="primary"
                  onClick={handleNextStep}
                >
                  {getNavigationButtonLabel()}
                </Button>
              </div>
            </Modal.Footer>
          )}
        </Modal>

        <ConfirmClosePopup
          isOpen={isConfirmCloseOpen}
          setIsOpen={setIsConfirmCloseOpen}
          handleClose={handleClose}
        />
        {alertDialog && (
          <AlertDialog
            {...alertDialog}
            isOpen={!!alertDialog}
            onClose={() => setAlertDialog(null)}
          />
        )}
      </TailwindWrapper>

    </>
  );
}
